import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronRight, Users, FileText, ClipboardList, Search,
  ShieldCheck, Award, Radio, Crosshair, Helicopter, CheckCircle2, XCircle,
} from "lucide-react";
import api from "../lib/api";
import { DIVISIONS } from "../data/divisions";

/**
 * Home LSSD — Editorial / Front-page istituzionale.
 * No hero fotografico dominante. Masthead sobrio + titolo centrale +
 * griglia editoriale di 3 tessere (1 foto + 2 tipografiche) + sezioni contenuti.
 */

const HERO_IMG = "/media/hero-explorer-still.jpg";
const TODAY = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

const SERVICES = [
  { to: "/arruolamento",  Icon: Users,        title: "Arruolamento",      desc: "Candidati per entrare nel Dipartimento.",           cta: "Candidati", badge: "Aperto" },
  { to: "/roster",        Icon: ClipboardList, title: "Organico",          desc: "Elenco pubblico degli operatori attivi.",           cta: "Consulta"  },
  { to: "/codice-penale", Icon: FileText,     title: "Codice Penale",     desc: "Normativa vigente della Contea di San Andreas.",    cta: "Sfoglia"   },
  { to: "/candidatura",   Icon: Search,       title: "Stato Candidatura", desc: "Verifica lo stato del tuo bando di reclutamento.",  cta: "Verifica"  },
];

export default function Home() {
  const [news, setNews] = useState([]);
  const [hof, setHof] = useState([]);
  const [recruitment, setRecruitment] = useState(null); // { open, message }

  useEffect(() => {
    api.get("/news").then(({ data }) => setNews(data.slice(0, 3))).catch(() => {});
    api.get("/hall-of-fame", { params: { limit: 4 } }).then(({ data }) => setHof(data)).catch(() => {});
    api.get("/settings/recruitment")
      .then(({ data }) => setRecruitment(data))
      .catch(() => setRecruitment({ open: true, message: null }));
  }, []);

  return (
    <div data-testid="home-page" className="bg-[var(--lssd-olive-deep)]">

      {/* ============ CINEMATIC HERO — full-viewport, foto di sfondo ============ */}
      <section
        className="relative min-h-[100dvh] w-full flex flex-col overflow-hidden bg-[var(--lssd-olive-deep)]"
        data-testid="hero"
      >
        {/* Foto di sfondo con overlay scuro editoriale */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMG}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          {/* Overlay a strati: vignetta laterale ESTESA (per leggibilità testo left) + tinta verde militare */}
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--lssd-olive-deep)] from-15% via-[var(--lssd-olive-deep)]/90 via-55% to-[var(--lssd-olive-deep)]/25 to-100%" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--lssd-olive-deep)] via-[var(--lssd-olive-deep)]/40 to-[var(--lssd-olive-deep)]/60" />
          {/* Grain texture leggero (SVG noise) */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
            style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.5'/></svg>\")" }}
          />
        </div>

        {/* Barra oro sottile in cima (over-image) */}
        <div className="relative z-10 h-[3px] w-full bg-gradient-to-r from-transparent via-[var(--lssd-khaki)] to-transparent" />

        {/* Edizione strip su fascia solida per leggibilità */}
        <div className="relative z-10 w-full border-b border-[var(--lssd-khaki)]/25" style={{ backgroundColor: "rgba(15, 26, 10, 0.85)" }}>
          <div className="max-w-7xl w-full mx-auto px-6 sm:px-10">
            <div className="flex items-center justify-between py-3 text-[10px] uppercase tracking-[0.22em] font-mono">
              <span className="text-[var(--lssd-khaki)] font-semibold">Edizione Ufficiale · Vol. CLXXII</span>
              <span className="hidden sm:inline capitalize text-[var(--lssd-cream)]/80">{TODAY}</span>
              <span className="text-[var(--lssd-khaki)] font-semibold">№ {new Date().getFullYear()}</span>
            </div>
          </div>
        </div>

        {/* Corpo hero — allineato a sinistra su desktop per feel editoriale */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-7xl w-full mx-auto px-6 sm:px-10 py-16 sm:py-20">
            <div className="max-w-4xl">

              {/* Badge stato bandi — pill isolata sopra il kicker per non spezzare la riga di brand */}
              {recruitment && (
                <div className="mb-5" data-testid="hero-recruitment-badge">
                  {recruitment.open ? (
                    <Link
                      to="/arruolamento"
                      data-testid="hero-recruitment-badge-open"
                      className="group inline-flex items-center gap-2 rounded-full border border-emerald-400/50 bg-emerald-500/12 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-200 hover:bg-emerald-500/22 hover:border-emerald-400/70 transition-colors"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                      </span>
                      Bandi Aperti
                      <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  ) : (
                    <span
                      data-testid="hero-recruitment-badge-closed"
                      className="inline-flex items-center gap-2 rounded-full border border-rose-400/45 bg-rose-500/12 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.24em] font-bold text-rose-200"
                    >
                      <span className="h-2 w-2 rounded-full bg-rose-400" />
                      Bandi Chiusi
                    </span>
                  )}
                </div>
              )}

              {/* Kicker istituzionale su riga dedicata */}
              <div className="flex items-center gap-3 mb-6" data-testid="hero-kicker">
                <span className="text-[var(--lssd-khaki)] text-xl leading-none">★</span>
                <span className="h-px w-14 bg-[var(--lssd-khaki)]/60" />
                <span className="text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--lssd-khaki)]">
                  Los Santos County · Sheriff&apos;s Department
                </span>
              </div>

              {/* Titolone display */}
              <h1
                data-testid="hero-title"
                className="font-display font-black text-[clamp(2.75rem,7.5vw,6.5rem)] text-[var(--lssd-cream)] leading-[0.92] tracking-tight"
              >
                A Tradition <br className="hidden sm:block" />
                of <span className="text-[var(--lssd-khaki)]">Service</span> <br />
                <span className="text-[var(--lssd-khaki)] italic font-medium">Since 1852.</span>
              </h1>

              {/* Motto */}
              <div className="mt-8 flex items-center gap-3">
                <span className="h-px w-16 bg-[var(--lssd-khaki)]/55" />
                <span className="text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--lssd-khaki)]">
                  Honor · Integrity · Service
                </span>
              </div>

              {/* Tagline */}
              <p className="mt-8 text-[15px] sm:text-lg text-[var(--lssd-cream)]/85 leading-relaxed max-w-2xl">
                Otto divisioni operative, oltre cinquanta operatori giurati.
                Al servizio della Contea di Los Santos dal <span className="text-[var(--lssd-khaki)] font-semibold">1852</span>.
              </p>

              {/* Doppio CTA editoriale */}
              <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="hero-ctas">
                <Link to="/arruolamento" data-testid="hero-cta-primary">
                  <span className="btn-gold">
                    Candidati Ora <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
                <Link to="/divisioni" data-testid="hero-cta-secondary">
                  <span className="btn-inst-outline">
                    Le Divisioni <ChevronRight className="h-4 w-4" />
                  </span>
                </Link>
              </div>

            </div>
          </div>
        </div>

        {/* Scroll indicator + stato bandi in basso */}
        <div className="relative z-10 border-t border-[var(--lssd-khaki)]/20" style={{ backgroundColor: "rgba(15, 26, 10, 0.65)" }}>
          <div className="max-w-7xl w-full mx-auto px-6 sm:px-10 py-4 flex items-center justify-between gap-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] font-semibold text-[var(--lssd-khaki)]/85 hidden sm:inline">
              Scorri per esplorare
            </span>
            <div className="flex items-center gap-3 mx-auto sm:mx-0">
              <span className="h-px w-8 bg-[var(--lssd-khaki)]/40" />
              <span className="text-[var(--lssd-khaki)] font-mono text-[10px] uppercase tracking-[0.28em] font-semibold animate-pulse">
                ▾
              </span>
              <span className="h-px w-8 bg-[var(--lssd-khaki)]/40" />
            </div>
            {recruitment ? (
              recruitment.open ? (
                <Link
                  to="/reclutamenti"
                  className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-300 hover:text-emerald-200 transition-colors"
                  data-testid="hero-scroll-recruitment-open"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                  </span>
                  Candidature Aperte
                </Link>
              ) : (
                <span
                  className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] font-bold text-rose-300"
                  data-testid="hero-scroll-recruitment-closed"
                >
                  <span className="h-2 w-2 rounded-full bg-rose-400" />
                  Candidature Sospese
                </span>
              )
            ) : (
              <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.28em] font-semibold text-[var(--lssd-cream)]/40">
                …
              </span>
            )}
          </div>
        </div>
      </section>

      {/* ============ STATS STRIP — sezione dedicata post-hero ============ */}
      <section className="border-b border-[var(--lssd-khaki)]/15 bg-[var(--lssd-olive-dark)]" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="py-6 border-b border-[var(--lssd-khaki)]/15">
            <div className="kicker text-center text-[var(--lssd-khaki)]">Il Dipartimento in cifre</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--lssd-khaki)]/15">
            {/* 3 stat statiche + 1 cella dinamica per lo stato bandi */}
            {[
              { v: "172", l: "Anni di Servizio" },
              { v: "50+", l: "Operatori Giurati" },
              { v: "8",   l: "Divisioni Operative" },
            ].map((s) => (
              <div key={s.l} className="py-8 px-4 text-center flex flex-col items-center relative group" data-testid={`stat-${s.l}`}>
                <div className="font-display font-black text-4xl sm:text-5xl text-[var(--lssd-khaki)] tracking-tight">
                  {s.v}
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--lssd-cream)]/70">
                  {s.l}
                </div>
              </div>
            ))}

            {/* Card stato bandi cliccabile */}
            {recruitment && recruitment.open ? (
              <Link
                to="/arruolamento"
                data-testid="stat-recruitment-open"
                className="py-8 px-4 text-center flex flex-col items-center justify-center relative group hover:bg-emerald-500/5 transition-colors"
              >
                <div className="flex items-center gap-2 font-display font-black text-3xl sm:text-4xl text-emerald-300 tracking-tight">
                  <CheckCircle2 className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={2.2} />
                  Aperti
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-200/85 inline-flex items-center gap-1">
                  Bandi di Arruolamento <ArrowRight className="h-3 w-3 opacity-70 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            ) : recruitment && !recruitment.open ? (
              <div
                data-testid="stat-recruitment-closed"
                className="py-8 px-4 text-center flex flex-col items-center justify-center relative"
              >
                <div className="flex items-center gap-2 font-display font-black text-3xl sm:text-4xl text-rose-300 tracking-tight">
                  <XCircle className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={2.2} />
                  Chiusi
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-rose-200/85">
                  Bandi Sospesi
                </div>
              </div>
            ) : (
              <div className="py-8 px-4 text-center flex flex-col items-center justify-center relative">
                <div className="font-display font-black text-4xl text-[var(--lssd-khaki)]/40">…</div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--lssd-cream)]/40">
                  Stato Bandi
                </div>
              </div>
            )}
          </div>
          {/* Messaggio dettaglio dal Comando quando presente */}
          {recruitment && recruitment.message && (
            <div className="py-4 border-t border-[var(--lssd-khaki)]/15 text-center" data-testid="recruitment-message">
              <span className="text-[13px] text-[var(--lssd-cream)]/80 italic">
                &ldquo;{recruitment.message}&rdquo;
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ============ EDITORIAL GRID — 3 tessere featured + strip completa 8 divisioni ============ */}
      <section className="py-16 sm:py-20 bg-[var(--lssd-olive-dark)] border-b border-[var(--lssd-khaki)]/15" data-testid="editorial-grid">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex items-baseline justify-between mb-10">
            <div>
              <div className="kicker mb-2">Sezione in Prima Pagina</div>
              <h2 className="font-display text-2xl sm:text-3xl text-[var(--lssd-cream)] tracking-tight">
                Le nostre divisioni al lavoro
              </h2>
            </div>
            <Link
              to="/divisioni"
              data-testid="editorial-all-divisions"
              className="hidden sm:inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--lssd-khaki)] hover:text-[var(--lssd-cream)] transition-colors"
            >
              Tutte le divisioni <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">

            {/* Tessera 1: foto grande (span 2 su desktop) */}
            <Link
              to="/divisioni/patrol"
              data-testid="editorial-card-patrol"
              className="group lg:col-span-2 lg:row-span-2 rounded-2xl overflow-hidden bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/25 hover:border-[var(--lssd-khaki)]/70 transition-all relative min-h-[380px] lg:min-h-[560px] flex flex-col justify-end"
            >
              <img
                src={HERO_IMG}
                alt="Patrol Division"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--lssd-olive-deep)]/95 via-[var(--lssd-olive-deep)]/40 to-transparent" />
              {/* Accento oro laterale */}
              <div className="absolute left-0 top-8 bottom-8 w-[3px] bg-[var(--lssd-khaki)] rounded-r-full" />

              <div className="relative p-7 sm:p-9">
                <div className="chip-inst mb-4 !bg-[var(--lssd-khaki)] !text-[var(--lssd-olive-deep)] !border-[var(--lssd-khaki)]">
                  <ShieldCheck className="h-3 w-3" /> Patrol Division
                </div>
                <h3 className="font-display font-black text-2xl sm:text-3xl text-[var(--lssd-cream)] leading-tight mb-2">
                  Presenza territoriale <br className="hidden sm:block" />
                  <span className="text-[var(--lssd-khaki)]">H24 · su tutta la Contea.</span>
                </h3>
                <p className="text-[var(--lssd-cream)]/80 text-sm sm:text-[15px] leading-relaxed max-w-xl">
                  La spina dorsale del Dipartimento. Prima risposta agli interventi,
                  pattugliamento capillare, presenza visibile nelle comunità.
                </p>
                <span className="mt-5 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-bold text-[var(--lssd-khaki)]">
                  Approfondisci <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>

            {/* Tessera 2: Aero Bureau con foto elicottero */}
            <Link
              to="/divisioni/aero"
              data-testid="editorial-card-aero"
              className="group rounded-2xl border border-[var(--lssd-khaki)]/20 hover:border-[var(--lssd-khaki)]/60 transition-all relative overflow-hidden flex flex-col min-h-[240px]"
            >
              <img
                src="/media/aero-helicopter.png"
                alt="Aero Bureau — Elicottero LSSD"
                className="absolute inset-0 w-full h-full object-cover scale-[1.35] transition-transform duration-700 group-hover:scale-[1.42]"
                style={{ objectPosition: "center 30%", transformOrigin: "center 70%" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              {/* gradient scuro in basso per leggibilità testo */}
              <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-[var(--lssd-olive-deep)] via-[var(--lssd-olive-deep)]/92 to-transparent" />
              <Helicopter className="absolute right-3 bottom-3 h-20 w-20 text-[var(--lssd-khaki)]/55 group-hover:text-[var(--lssd-khaki)]/80 transition-colors drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" strokeWidth={1.75} />
              <div className="relative mt-auto p-6">
                <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--lssd-khaki)] mb-3">
                  <Helicopter className="h-3 w-3" strokeWidth={2.2} /> Unit · AERO
                </div>
                <h3 className="font-display font-bold text-xl text-[var(--lssd-cream)] leading-tight mb-2">
                  Aero Bureau
                </h3>
                <p className="text-[var(--lssd-cream)]/75 text-[13px] leading-relaxed mb-4 max-w-xs">
                  Supporto aereo, ricerca dispersi, sorveglianza tattica e SAR sopra la Contea.
                </p>
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--lssd-khaki)]">
                  Scopri <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>

            {/* Tessera 3: tipografica SEB */}
            <Link
              to="/divisioni/seb"
              data-testid="editorial-card-seb"
              className="group rounded-2xl bg-gradient-to-br from-[var(--lssd-olive-deep)] to-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/20 hover:border-[var(--lssd-khaki)]/60 transition-all p-7 relative overflow-hidden flex flex-col min-h-[240px]"
            >
              <Crosshair className="absolute -right-4 -bottom-6 h-40 w-40 text-[var(--lssd-khaki)]/8 group-hover:text-[var(--lssd-khaki)]/14 transition-colors" />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] font-semibold text-[var(--lssd-khaki)] mb-3">
                  Unit · SEB
                </div>
                <h3 className="font-display font-bold text-xl text-[var(--lssd-cream)] leading-tight mb-2">
                  Special Enforcement
                </h3>
                <p className="text-[var(--lssd-cream)]/65 text-[13px] leading-relaxed mb-5">
                  Unità tattica ad alto rischio: ostaggi, terrorismo, irruzioni armate, protezione dignitari.
                </p>
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--lssd-khaki)]">
                  Scopri <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          </div>

          {/* Strip completa con tutte e 8 le divisioni */}
          <div className="mt-8 sm:mt-10 pt-8 sm:pt-10 border-t border-[var(--lssd-khaki)]/15">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
              <div>
                <div className="kicker mb-1">Tutte le unità operative</div>
                <p className="text-[var(--lssd-cream)]/60 text-sm">
                  Otto divisioni specializzate. Clicca per la descrizione dettagliata di ciascuna.
                </p>
              </div>
              <Link
                to="/divisioni"
                data-testid="editorial-strip-index"
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--lssd-khaki)] hover:text-[var(--lssd-cream)] transition-colors"
              >
                Pagina indice <ArrowRight className="h-3 w-3" />
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {DIVISIONS.map((d) => {
                const Icon = d.icon;
                return (
                  <Link
                    key={d.code}
                    to={`/divisioni/${d.slug}`}
                    data-testid={`home-division-chip-${d.code}`}
                    className="group flex items-start gap-3 bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/20 rounded-xl p-4 hover:border-[var(--lssd-khaki)]/55 hover:-translate-y-0.5 transition-all"
                  >
                    <div className="shrink-0 h-9 w-9 rounded-lg bg-[var(--lssd-khaki)]/12 border border-[var(--lssd-khaki)]/30 flex items-center justify-center group-hover:bg-[var(--lssd-khaki)]/22 transition-colors">
                      <Icon className="h-4 w-4 text-[var(--lssd-khaki)]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[9px] uppercase tracking-[0.22em] font-bold text-[var(--lssd-khaki)] leading-none mb-1.5">
                        {d.code}
                      </div>
                      <div className="font-display font-bold text-[13px] text-[var(--lssd-cream)] leading-tight truncate">
                        {d.name}
                      </div>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-[var(--lssd-khaki)]/50 group-hover:text-[var(--lssd-khaki)] group-hover:translate-x-0.5 transition-all shrink-0 self-center" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVIZI PUBBLICI ============ */}
      <section className="bg-[var(--lssd-olive-deep)] py-16 sm:py-20" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="kicker mb-2">Servizi al Cittadino</div>
              <h2 className="font-display text-2xl sm:text-3xl text-[var(--lssd-cream)] tracking-tight">
                In cosa possiamo aiutarti
              </h2>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {SERVICES.map((s) => {
              const Icon = s.Icon;
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  data-testid={`service-${s.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="group flex flex-col rounded-2xl bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/20 hover:border-[var(--lssd-khaki)]/60 p-6 transition-all relative overflow-hidden"
                >
                  {/* accento superiore */}
                  <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[var(--lssd-khaki)]/60 to-transparent" />
                  {s.badge && (
                    <span className="absolute top-4 right-4 rounded-full bg-[var(--lssd-khaki)]/20 border border-[var(--lssd-khaki)]/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--lssd-khaki)] font-bold">
                      {s.badge}
                    </span>
                  )}
                  <div className="h-10 w-10 rounded-full bg-[var(--lssd-khaki)]/18 border border-[var(--lssd-khaki)]/45 flex items-center justify-center mb-4">
                    <Icon className="h-4 w-4 text-[var(--lssd-khaki)]" />
                  </div>
                  <div className="font-display text-base font-bold text-[var(--lssd-cream)] mb-1.5">
                    {s.title}
                  </div>
                  <div className="text-[13px] text-[var(--lssd-cream)]/60 leading-relaxed flex-1 mb-4">
                    {s.desc}
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--lssd-khaki)]">
                    {s.cta} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ NEWS EDITORIALE ============ */}
      <section className="bg-[var(--lssd-olive-dark)] border-y border-[var(--lssd-khaki)]/15 py-16 sm:py-20" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="kicker mb-2">Bollettino del Comando</div>
              <h2 className="font-display text-2xl sm:text-3xl text-[var(--lssd-cream)] tracking-tight">
                Ultime Comunicazioni
              </h2>
            </div>
            {news.length > 0 && (
              <Link to="/" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--lssd-khaki)] hover:text-[var(--lssd-cream)] transition-colors inline-flex items-center gap-1">
                Tutti i comunicati <ChevronRight className="h-4 w-4" />
              </Link>
            )}
          </div>

          {news.length === 0 ? (
            <div className="rounded-2xl bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/15 py-16 text-center text-[var(--lssd-cream)]/45">
              Nessun comunicato pubblicato al momento.
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4 sm:gap-5" data-testid="news-grid">
              {news.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  data-testid={`news-card-${n.id}`}
                  className="group flex flex-col rounded-2xl bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/15 hover:border-[var(--lssd-khaki)]/50 transition-all overflow-hidden"
                >
                  {n.image_path ? (
                    <div className="aspect-[16/10] overflow-hidden bg-[var(--lssd-olive-deep)]">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/news/image/${n.image_path}`}
                        alt={n.title}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-gradient-to-br from-[var(--lssd-olive-deep)] to-[var(--lssd-olive)] flex items-center justify-center">
                      <ShieldCheck className="h-12 w-12 text-[var(--lssd-khaki)]/20" />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--lssd-khaki)] mb-3">
                      {new Date(n.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                      {n.author && <>
                        <span className="opacity-40">·</span>
                        <span className="text-[var(--lssd-cream)]/50 normal-case tracking-normal">{n.author}</span>
                      </>}
                    </div>
                    <h3 className="font-display text-lg font-bold text-[var(--lssd-cream)] leading-snug mb-3 group-hover:text-[var(--lssd-khaki)] transition-colors line-clamp-2">
                      {n.title}
                    </h3>
                    <p className="text-[var(--lssd-cream)]/60 text-[13px] leading-relaxed flex-1 line-clamp-3">
                      {n.content}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--lssd-khaki)]">
                      Leggi tutto <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============ HALL OF FAME ============ */}
      {hof.length > 0 && (
        <section className="bg-[var(--lssd-olive-deep)] py-16 sm:py-20" data-testid="hall-of-fame">
          <div className="max-w-7xl mx-auto px-6 sm:px-10">
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="kicker mb-2 inline-flex items-center gap-1.5">
                  <Award className="h-3.5 w-3.5" /> Hall of Fame
                </div>
                <h2 className="font-display text-2xl sm:text-3xl text-[var(--lssd-cream)] tracking-tight">
                  Recenti Promozioni
                </h2>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {hof.map((p, i) => {
                const date = p.promoted_at ? new Date(p.promoted_at) : null;
                return (
                  <div
                    key={`${p.rp_name}-${p.promoted_at}-${i}`}
                    data-testid={`hof-card-${i}`}
                    className="rounded-2xl bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/15 p-5 hover:border-[var(--lssd-khaki)]/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--lssd-khaki)]/85">
                        № {String(i + 1).padStart(2, "0")}
                      </span>
                      <Award className="h-3.5 w-3.5 text-[var(--lssd-khaki)]" />
                    </div>
                    <div className="font-display text-base font-bold text-[var(--lssd-cream)] mb-2 truncate">
                      {p.rp_name}
                    </div>
                    <div className="text-[12px] text-[var(--lssd-cream)]/55 mb-4 leading-snug">
                      {p.from_role}
                      <ChevronRight className="inline h-3 w-3 mx-1 text-[var(--lssd-khaki)]" />
                      <span className="text-[var(--lssd-khaki)] font-medium">{p.to_role}</span>
                    </div>
                    <div className="pt-3 border-t border-[var(--lssd-khaki)]/15 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--lssd-cream)]/45">
                      {date ? date.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============ CTA FINALE ============ */}
      <section className="bg-[var(--lssd-olive-dark)] border-t border-[var(--lssd-khaki)]/25 py-16 sm:py-20 relative overflow-hidden" data-testid="cta-section">
        {/* Bagliori khaki di sfondo */}
        <div className="absolute -left-40 top-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-[var(--lssd-khaki)]/8 blur-3xl pointer-events-none" />
        <div className="absolute -right-40 top-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-[var(--lssd-khaki)]/6 blur-3xl pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--lssd-khaki)] to-transparent" />

        <div className="relative max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <div className="kicker mb-3">Reclutamenti Aperti</div>
          <h2 className="font-display font-black text-3xl sm:text-5xl text-[var(--lssd-cream)] leading-[1.05] tracking-tight mb-5">
            Servi con onore. <span className="text-[var(--lssd-khaki)]">Servi con noi.</span>
          </h2>
          <p className="text-[var(--lssd-cream)]/75 max-w-2xl mx-auto leading-relaxed mb-8">
            Ogni operatore è custode di una tradizione lunga oltre <span className="text-[var(--lssd-khaki)] font-semibold">170 anni</span>.
            Se hai le qualità per indossare la stella, invia la tua candidatura.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/arruolamento" data-testid="cta-final-recruitment">
              <span className="btn-gold">Invia Candidatura <ArrowRight className="h-4 w-4" /></span>
            </Link>
            <Link to="/codice-penale" data-testid="cta-final-penal">
              <span className="btn-inst-outline"><FileText className="h-4 w-4" /> Consulta il Codice Penale</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
